/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/no-unsafe-argument */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
import axios, { AxiosError, type InternalAxiosRequestConfig } from "axios";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { getLanguageCode, type LanTypes } from "@src/helpers/index";
import * as Sentry from "@sentry/react";

export interface ErrorData {
  code?: number;
  displayText?: string;
  errorMessage?: string;
  Code?: number;
  DisplayText?: string;
  ErrorMessage?: string;
}

const axiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

const AxiosProvider = ({ children }: { children: JSX.Element }) => {
  const { i18n } = useTranslation();

  useEffect(() => {
    const beforeRequest = (config: InternalAxiosRequestConfig) => {
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      if (config.headers) {
        if (!config.headers.langcode) {
          config.headers.langcode = getLanguageCode(i18n.language as LanTypes);
        }
      }

      return config;
    };

    const errInterceptor = (error: AxiosError<ErrorData>) => {
      const url = error.config?.url ?? "";
      const status = error.response?.status;

      if (
        !url.includes("sentry.io") &&
        (status === undefined || status >= 500)
      ) {
        Sentry.captureException(error, {
          extra: {
            url: error.config?.url,
            method: error.config?.method,
            status,
            response: error.response?.data,
          },
        });
      }
      // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors
      return Promise.reject({
        ...error,
        response: {
          ...error.response,
          data: {
            code: error.response?.data.code || error.response?.data.Code,
            displayText:
              error.response?.data.displayText ||
              error.response?.data.DisplayText,
            errorMessage:
              error.response?.data.errorMessage ||
              error.response?.data.ErrorMessage,
          },
        },
      });
    };

    const response = axiosInstance.interceptors.response.use((r) => {
      if (r.data?.Ok === false || r.data.ok === false) {
        const error = r.data.Errors?.[0] || r.data.errors?.[0];

        const errorObject = new AxiosError(
          error?.errorMessage || error?.ErrorMessage
        );

        const url = error.config?.url ?? "";

        if (
          !url.includes("sentry.io") &&
          (!error.response || error.response.status >= 500)
        ) {
          Sentry.captureException(error, {
            extra: {
              url: error.config?.url,
              method: error.config?.method,
              data: error.config?.data,
              status: error.response?.status,
              response: error.response?.data,
              businessError: {
                code: error?.code || error?.Code,
                displayText: error?.displayText || error?.DisplayText,
                errorMessage: error?.errorMessage || error?.ErrorMessage,
              },
            },
          });
        }

        // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors
        return Promise.reject({
          ...errorObject,
          response: {
            data: {
              code: error?.code || error?.Code,
              displayText: error?.displayText || error?.Displaytext,
              errorMessage: error?.errorMessage || error?.ErrorMessage,
            },
          },
        });
      }

      return r;
    }, errInterceptor);
    const request = axiosInstance.interceptors.request.use(beforeRequest);

    return () => {
      axiosInstance.interceptors.response.eject(response);
      axiosInstance.interceptors.request.eject(request);
    };
  }, [i18n.language]);

  return children;
};

export default AxiosProvider;
export { axiosInstance };
